import { queryOptions } from '@tanstack/react-query'
import { apiGet, apiPost, type PostValue } from './client'

/**
 * Settings -> Agent Autonomy -> Email intake (v4/api, `App\Mail\Intake\
 * MailIntakeSettings`): casuta pe care o citeste platforma pentru cererile
 * catre Adam, pe firma (`scope=company`) sau a furnizorului (`scope=sp`).
 * Planul: docs/plan-adam-prin-email.md.
 */
const BASE = '/settings/mail_intake'

export type MailIntakeKind = 'graph' | 'imap'

export interface MailIntakeSetting {
  enabled: boolean
  mode: 'shadow' | 'live'
  kind: MailIntakeKind
  address: string
  graph_tenant_id: string | null
  imap_host: string | null
  imap_port: number
  imap_tls: boolean
  imap_user: string | null
  password_set: boolean
  folder_in: string
  folder_done: string
  folder_failed: string
  unknown_senders: string
  require_dmarc: boolean
  reply_mode: string
  max_per_sender_day: number
  last_poll_at: string | null
  last_ok_at: string | null
  last_error: string | null
  fail_count: number
  checked_at: string | null
  check_ok: boolean | null
  check_error: string | null
  /** Microsoft 365: aplicatia e ingradita la casuta asta (true), poate citi si altele (false), neprobat (null). */
  scope_ok?: boolean | null
}

export interface MailIntakePage {
  ready: boolean
  scope: 'company' | 'sp'
  isGlobal: boolean
  sp: number
  companyId: number
  cryptoReady: boolean
  graphConfigured: boolean
  imapAvailable: boolean
  m365Tenant: string | null
  kinds: MailIntakeKind[]
  unknownSenders: string[]
  replyModes: string[]
  modes?: string[]
  setting: MailIntakeSetting | null
}

export interface MailIntakeMessage {
  id: number
  company_id: number
  from: string
  subject: string
  received_at: string
  dmarc: string | null
  sender_kind: string
  decision: string
  reason: string
  ticket_id: number
  /** Cate atasamente au intrat in tichet. */
  attachments?: number
  shadow: boolean
  /** Un mesaj picat al unei casute Microsoft 365 live: se poate relua. */
  retry?: boolean
}

export interface MailIntakeTest {
  ok: boolean
  error?: string
  folders: string[]
  missing_folders: string[]
  scope_ok: boolean | null
}

export const mailIntakeKey = (scope: string) => ['settings', 'mail_intake', scope] as const

export function mailIntakeQuery(scope: string) {
  return queryOptions({
    queryKey: mailIntakeKey(scope),
    queryFn: () => apiGet<MailIntakePage>(BASE + (scope ? `?scope=${encodeURIComponent(scope)}` : '')),
  })
}

export const mailIntakeMessagesKey = (scope: string) => ['settings', 'mail_intake', 'messages', scope] as const

export function mailIntakeMessagesQuery(scope: string) {
  return queryOptions({
    queryKey: mailIntakeMessagesKey(scope),
    queryFn: () => apiGet<{ scope: string; rows: MailIntakeMessage[] }>(`${BASE}/messages` + (scope ? `?scope=${encodeURIComponent(scope)}` : '')),
  })
}

export interface MailIntakeOutboxRow {
  id: number
  company_id: number
  ticket_id: number
  to: string
  subject: string
  state: string
  attempts: number
  sent_at: string | null
  error: string
  created_at: string
  message_id_set: boolean
}

export const mailIntakeOutboxKey = (scope: string) => ['settings', 'mail_intake', 'outbox', scope] as const

export function mailIntakeOutboxQuery(scope: string) {
  return queryOptions({
    queryKey: mailIntakeOutboxKey(scope),
    queryFn: () => apiGet<{ scope: string; rows: MailIntakeOutboxRow[] }>(`${BASE}/outbox` + (scope ? `?scope=${encodeURIComponent(scope)}` : '')),
  })
}

export const mailIntakeSave = (fields: Record<string, PostValue>) => apiPost<MailIntakePage>(`${BASE}/save`, fields)
export const mailIntakeTest = (scope: string) => apiPost<MailIntakeTest>(`${BASE}/test`, { scope })
/** Reia un mesaj picat (numai Microsoft 365, casuta live). */
export const mailIntakeRetry = (scope: string, id: number) => apiPost<{ ok: boolean; decision?: string; error?: string }>(`${BASE}/messages/${id}/retry`, { scope })
export const mailIntakeResend = (scope: string, id: number) => apiPost<{ ok: boolean; error?: string }>(`${BASE}/outbox/${id}/resend`, { scope })
export const mailIntakeDelete = (scope: string) => apiPost<MailIntakePage>(`${BASE}/delete`, { scope })
